import SpeechmaticsAudio from '../audio/speechmatics-audio';

class SpeechmaticsTranscribe {
    constructor(getToken) {
        this.getToken = getToken

        const mimeTypes = [
            'audio/webm;codecs=opus',
            'audio/ogg;codecs=opus',
            'audio/mpeg',
        ];
        this.audio = new SpeechmaticsAudio(mimeTypes)
        this.websocket = null
        this.isFinalizingTranscript = false
        this.audioSeqNo = 0

        this.bindEvents()
    }

    /**
     * Select microphone to use in recording
     * @param deviceId Id of selected microphone device
     */
    setSelectedDevice(deviceId) {
        this.audio.setSelectedDevice(deviceId)
    }

    bindEvents() {
        document.addEventListener('ui:microphoneChanged', (e) => {
            this.audio.setSelectedDevice(e.detail)
        })
    }

    async startRecording(language, diarizationEnabled) {
        this.isFinalizingTranscript = false
        const { token } = await this.getToken()
        this.websocket = new WebSocket(`wss://eu2.rt.speechmatics.com/v2?jwt=${token}`)

        this.audioSeqNo = 0

        this.websocket.onopen = () => {
            const startMessage = {
                message: "StartRecognition",
                audio_format: {
                    type: "file"
                },
                transcription_config: {
                    language: language,
                    operating_point: "enhanced",
                    enable_partials: true,
                    ...(diarizationEnabled ? {
                        diarization: "speaker",
                        speaker_diarization_config: {
                            max_speakers: 5,
                            speaker_sensitivity: 0.5
                        }
                    } : {})
                }
            }
            this.websocket.send(JSON.stringify(startMessage))
        }

        this.audio.ondataavailable = (event) => {
            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                this.audioSeqNo++;
                this.websocket.send(event.data);
            }
        }

        this.websocket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            this.handleWebSocketMessage(message);
        };

        this.audio.record()

        this.onRecordingStarted?.()
    }

    stopRecording() {
        this.audio.stop()

        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.isFinalizingTranscript = true;  // Set flag to indicate we're waiting for final transcripts
            const endMessage = {
                message: "EndOfStream",
                last_seq_no: this.audioSeqNo
            };
            this.websocket.send(JSON.stringify(endMessage));
        }

        this.onRecordingStopped?.()
    }

    parseSpeakerChunks(message) {
        let chunkData = {}
        // In Speechmatics documentation it seems results could hold
        // chunks from multiple speakers.
        for (const result of message.results) {
            // Alternatives can be a list of words / punctuation with varying
            // confidence values. We just pick the one with most confidence
            // which should be the first item in the array.
            if (!result.alternatives) {
                continue
            }

            const mostProbable = result.alternatives[0]
            if (!mostProbable.content || mostProbable.content.length === 0) {
                continue
            }
            const speaker = mostProbable.speaker

            if (!chunkData[speaker]) {
                chunkData[speaker] = ""
            }
            
            if (result.type === "word") {
                chunkData[speaker] += mostProbable.content + " "
            } else if (result.type === "punctuation") {
                if (result.attaches_to === "previous") {
                    if (chunkData[speaker].length > 0 && chunkData[speaker].endsWith(" ")) {
                        chunkData[speaker] = chunkData[speaker].slice(0, chunkData[speaker].length - 1) + mostProbable.content + " "
                    } else {
                        chunkData[speaker] += mostProbable.content + " "
                    }
                } else {
                    chunkData[speaker] += mostProbable.content
                }
            }
        }

        return chunkData
    }

    handleWebSocketMessage(message) {
        switch (message.message) {
            case 'RecognitionStarted':
                break;

            case 'AddPartialTranscript':
                {
                    if (!message.metadata.transcript.trim()) {
                        return;
                    }
                    const chunkData = this.parseSpeakerChunks(message)
                    for (const speaker of Object.keys(chunkData)) {
                        this.onTranscriptionUpdate?.({
                            type: 'partial',
                            text: chunkData[speaker],
                            speaker: speaker
                        })
                    }
                }
                break;

            case 'AddTranscript':
                {
                    if (!message.metadata.transcript.trim()) {
                        return;
                    }
                    if (message.results.length === 0) {
                        console.log('No results:', message);
                        return;
                    }

                    const chunkData = this.parseSpeakerChunks(message)
                    for (const speaker of Object.keys(chunkData)) {
                        this.onTranscriptionUpdate?.({
                            type: 'final',
                            text: chunkData[speaker],
                            speaker: speaker
                        })
                    }
                }
                break;

            case 'EndOfTranscript':
                if (this.isFinalizingTranscript) {
                    this.isFinalizingTranscript = false;
                    if (this.websocket) {
                        this.websocket.close();
                    }
                }
                break;

            case 'Error':
                this.onTranscriptionUpdate?.(null, `Speechmatics error: ${message.reason}`)
                this.isFinalizingTranscript = false;
                if (this.websocket) {
                    this.websocket.close();
                }
                break;

            case 'Warning':
                console.warn('Speechmatics warning:', message.reason);
                break;

            case 'Info':
                if (message.type === 'concurrent_session_usage') {
                    this.onConcurrencyUpdate?.({
                        usage: message.usage,
                        quota: message.quota,
                        last_updated: message.last_updated,
                    });
                }

                console.info('Speechmatics info:', message.reason);
                break;
        }
    }
}

export default SpeechmaticsTranscribe
